"use client";

import { useEffect, useRef, useState } from "react";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** False when no site key is set, in which case forms work without a token. */
export const TURNSTILE_ENABLED = !!SITE_KEY;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript() {
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Turnstile failed to load"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  action: "login" | "contact";
  onTokenChange: (token: string | null) => void;
}

/**
 * Invisible for most people; Cloudflare only shows it when it needs an
 * interaction. Tokens are single-use: change this component's `key` to get a
 * fresh one after each submit.
 */
export function TurnstileWidget({ action, onTokenChange }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    if (!SITE_KEY) return;

    let cancelled = false;
    let widgetId: string | undefined;

    const fail = () => {
      setFailed(true);
      onTokenChangeRef.current(null);
    };

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action,
          theme: "auto",
          size: "flexible",
          appearance: "interaction-only",
          callback: (token: string) => {
            setFailed(false);
            onTokenChangeRef.current(token);
          },
          "expired-callback": () => onTokenChangeRef.current(null),
          "error-callback": fail,
          "timeout-callback": fail,
          "unsupported-callback": fail,
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (widgetId) window.turnstile?.remove(widgetId);
    };
  }, [action]);

  if (!SITE_KEY) return null;

  return (
    <div>
      <div ref={containerRef} />
      {failed && (
        <p role="alert" className="text-[12px] leading-relaxed text-red-500">
          We couldn&apos;t confirm you&apos;re human. Turn off any ad blocker or
          VPN and refresh, or email support@authorflows.com.
        </p>
      )}
    </div>
  );
}
